import config from 'config';

export const LANGUAGES_BY_COUNTRY = {
  ar: ['es-AR'],
  at: ['de-AT'],
  be: ['fr-BE', 'nl-BE'],
  br: ['pt-BR'],
  ca: ['en-CA', 'fr-CA'],
  ch: ['fr-CH', 'de-CH'],
  cl: ['es-CL'],
  de: ['de'],
  es: ['es-ES'],
  fr: ['fr-FR'],
  gb: ['en-GB'],
  ie: ['en-IE'],
  it: ['it'],
  lu: ['fr-LU', 'de-LU'],
  mx: ['es-MX'],
  nl: ['nl-NL'],
  pt: ['pt-PT'],
  ro: ['ro'],
  us: ['en-US'],
} as const;

export type Country = keyof typeof LANGUAGES_BY_COUNTRY;

const ALL_COUNTRIES = Object.keys(LANGUAGES_BY_COUNTRY) as Country[];
const ALL_LANGUAGES = Object.values(LANGUAGES_BY_COUNTRY).flat();

export type Language =
  | (typeof ALL_LANGUAGES)[number]
  | 'en'
  | 'es'
  | 'fr'
  | 'pt'
  | 'de';

/**
 * The inverse of `LANGUAGES_BY_COUNTRY`: which country a given `Language`
 * belongs to. Hand-written rather than derived so `satisfies Record<Language,
 * Country>` can enforce that every `Language` value has an entry — a new
 * language added to the union without updating this map is a compile error,
 * not a silent `undefined` at runtime.
 *
 * The five bare language codes (`en`, `es`, `fr`, `pt`, `de`) aren't in any
 * `LANGUAGES_BY_COUNTRY` entry, so they fall back to the same defaults
 * `gympassWebsiteLanguages` (utils/redirects.ts) uses for them.
 */
const COUNTRY_BY_LANGUAGE = {
  'es-AR': 'ar',
  'de-AT': 'at',
  'fr-BE': 'be',
  'nl-BE': 'be',
  'pt-BR': 'br',
  'en-CA': 'ca',
  'fr-CA': 'ca',
  'fr-CH': 'ch',
  'de-CH': 'ch',
  'es-CL': 'cl',
  de: 'de',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'en-GB': 'gb',
  'en-IE': 'ie',
  it: 'it',
  'fr-LU': 'lu',
  'de-LU': 'lu',
  'es-MX': 'mx',
  'nl-NL': 'nl',
  'pt-PT': 'pt',
  ro: 'ro',
  'en-US': 'us',
  en: 'us',
  es: 'es',
  fr: 'fr',
  pt: 'br',
} as const satisfies Record<Language, Country>;

export function resolveCountryFromLanguage(language: Language): Country {
  return COUNTRY_BY_LANGUAGE[language];
}

/**
 * Get available countries filtered by AVAILABLE_COUNTRIES env var.
 * Uses lazy evaluation to support runtime env loading.
 */
export function getAvailableCountries(): Country[] {
  const envValue = config.AVAILABLE_COUNTRIES;

  if (!envValue) {
    return ALL_COUNTRIES;
  }

  const parsed = envValue
    .trim()
    .split(',')
    .map((s: string) => s.trim()) as Country[];
  return parsed.filter(country =>
    (ALL_COUNTRIES as readonly string[]).includes(country),
  );
}

export const countriesWithNativeName: Record<Country, string> = {
  ar: 'Argentina',
  br: 'Brasil',
  cl: 'Chile',
  de: 'Deutschland',
  es: 'España',
  ie: 'Ireland',
  it: 'Italia',
  mx: 'México',
  gb: 'United Kingdom',
  us: 'United States',
  ca: 'Canada',
  pt: 'Portugal',
  ro: 'România',
  at: 'Österreich',
  be: 'België',
  ch: 'Schweiz',
  fr: 'France',
  lu: 'Luxemburg',
  nl: 'Nederland',
};

export type Currency = {
  iso:
    | 'ARS'
    | 'BRL'
    | 'CLP'
    | 'EUR'
    | 'MXN'
    | 'GBP'
    | 'USD'
    | 'CAD'
    | 'RON'
    | 'CHF';
  symbol: '$' | 'R$' | '€' | '£' | 'lei' | 'CHF';
};

export const currencyByCountry: Record<Country, Currency> = {
  ar: {
    iso: 'ARS',
    symbol: '$',
  },
  br: {
    iso: 'BRL',
    symbol: 'R$',
  },
  ca: {
    iso: 'CAD',
    symbol: '$',
  },
  cl: {
    iso: 'CLP',
    symbol: '$',
  },
  de: {
    iso: 'EUR',
    symbol: '€',
  },
  es: {
    iso: 'EUR',
    symbol: '€',
  },
  ie: {
    iso: 'EUR',
    symbol: '€',
  },
  it: {
    iso: 'EUR',
    symbol: '€',
  },
  mx: {
    iso: 'MXN',
    symbol: '$',
  },
  gb: {
    iso: 'GBP',
    symbol: '£',
  },
  pt: {
    iso: 'EUR',
    symbol: '€',
  },
  ro: {
    iso: 'RON',
    symbol: 'lei',
  },
  us: {
    iso: 'USD',
    symbol: '$',
  },
  at: {
    iso: 'EUR',
    symbol: '€',
  },
  be: {
    iso: 'EUR',
    symbol: '€',
  },
  ch: {
    iso: 'CHF',
    symbol: 'CHF',
  },
  fr: {
    iso: 'EUR',
    symbol: '€',
  },
  lu: {
    iso: 'EUR',
    symbol: '€',
  },
  nl: {
    iso: 'EUR',
    symbol: '€',
  },
};

export const defaultLanguage: Language = 'en';
